import React, { useEffect, useRef, useState } from 'react'

import { DayPicker } from '@daypicker/react'
import clsx from 'clsx'
import { format, isValid, parse, subYears } from 'date-fns'
import { Calendar } from 'lucide-react'
import ReactDOM from 'react-dom'

import { mergeRefs } from 'Utils'

import '@daypicker/react/style.css'

interface DateFieldProps {
  autoComplete?: string
  error?: boolean
  id: string
  inputRef?: React.Ref<HTMLInputElement>
  name: string
  onBlur?: () => void
  onChange: (value: string) => void
  popoverLabel?: string
  value: string
}

interface PopCoords {
  left: number
  top: number
}

const ISO = 'yyyy-MM-dd'
const DISPLAY = 'MMM d, yyyy'
const START_MONTH = new Date(1900, 0)
const DEFAULT_YEARS_AGO = 30
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(', ')

const toDate = (value: string): Date | undefined => {
  if (!value) return undefined
  const parsed = parse(value, ISO, new Date())
  return isValid(parsed) ? parsed : undefined
}

const getFocusable = (container: HTMLElement | null): HTMLElement[] =>
  container ? Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : []

export const DateField: React.FC<DateFieldProps> = ({
  autoComplete,
  error,
  id,
  inputRef,
  name,
  onBlur,
  onChange,
  popoverLabel = 'Choose a date',
  value,
}) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const localInputRef = useRef<HTMLInputElement | null>(null)
  const selected = toDate(value)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<PopCoords>({ left: 0, top: 0 })
  const [month, setMonth] = useState<Date>(selected || subYears(new Date(), DEFAULT_YEARS_AGO))

  // Keep the displayed month in sync when `value` changes externally (e.g.
  // a form reset) — adjusted during render rather than in an effect.
  const [prevValue, setPrevValue] = useState(value)
  if (value !== prevValue) {
    setPrevValue(value)
    if (selected) setMonth(selected)
  }

  useEffect(() => {
    if (!open) return undefined

    const place = () => {
      const box = rootRef.current?.getBoundingClientRect()
      if (!box) return
      const popHeight = popRef.current?.offsetHeight || 360
      const gap = 4
      const fitsBelow = box.bottom + gap + popHeight <= window.innerHeight
      setCoords({
        left: box.left,
        top: fitsBelow ? box.bottom + gap : Math.max(8, box.top - popHeight - gap),
      })
    }

    const closeAndFocusInput = () => {
      setOpen(false)
      localInputRef.current?.focus()
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (rootRef.current?.contains(target) || popRef.current?.contains(target)) {
        return
      }
      setOpen(false)
      if (onBlur) onBlur()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        closeAndFocusInput()
        return
      }

      if (event.key !== 'Tab') return

      const focusables = getFocusable(popRef.current)
      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement

      if (event.shiftKey) {
        if (active === first || !popRef.current?.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last || !popRef.current?.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }

    place()
    window.addEventListener('resize', place)
    document.addEventListener('scroll', place, true)
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.removeEventListener('resize', place)
      document.removeEventListener('scroll', place, true)
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [onBlur, open])

  const openCalendar = () => {
    setMonth(selected || subYears(new Date(), DEFAULT_YEARS_AGO))
    setOpen(true)
  }

  return (
    <div ref={rootRef} className={clsx('date-field', open && 'date-field_open')}>
      <input
        ref={mergeRefs(localInputRef, inputRef)}
        readOnly
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-invalid={Boolean(error)}
        autoComplete={autoComplete}
        className="date-field__control"
        id={id}
        name={name}
        placeholder="Choose a date"
        type="text"
        value={selected ? format(selected, DISPLAY) : ''}
        onBlur={() => {
          if (!open && onBlur) onBlur()
        }}
        onClick={openCalendar}
        onKeyDown={(event) => {
          if (open) return
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault()
            openCalendar()
          }
        }}
      />
      <button
        aria-label="Open calendar"
        className="date-field__button"
        tabIndex={-1}
        type="button"
        onClick={() => {
          if (open) setOpen(false)
          else openCalendar()
        }}
      >
        <Calendar aria-hidden size={18} strokeWidth={1.75} />
      </button>
      {open
        ? ReactDOM.createPortal(
            <div
              ref={popRef}
              aria-label={popoverLabel}
              aria-modal="true"
              className="date-field__pop"
              role="dialog"
              style={{ left: coords.left, top: coords.top }}
            >
              <DayPicker
                animate
                autoFocus
                reverseYears
                captionLayout="dropdown"
                disabled={{ after: new Date() }}
                endMonth={new Date()}
                mode="single"
                month={month}
                navLayout="after"
                selected={selected}
                startMonth={START_MONTH}
                onMonthChange={setMonth}
                onSelect={(date) => {
                  onChange(date ? format(date, ISO) : '')
                  setOpen(false)
                  localInputRef.current?.focus()
                  if (onBlur) onBlur()
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
